import React, { useState, useEffect } from "react";
import { Form, Button, Table, Spinner, Alert, Card, Row, Col } from "react-bootstrap";
import API from "../../services/api";

const FoodSalesReport = () => {
    const today = new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const summary = items.reduce(
        (acc, item) => {
            acc.quantity += Number(item.quantity || 0);
            acc.sales += Number(item.total_sales || 0);

            return acc;
        },
        {
            quantity: 0,
            sales: 0,
        }
    );

    // =========================
    // Fetch Food Report
    // =========================
    const fetchFoodReport = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                from: fromDate,
                to: toDate,
            };

            const res = await API.get("/food-report", { params });

            setItems(res.data.data || []);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to load food sales report"
            );

            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchFoodReport();

        // eslint-disable-next-line
    }, []);

    // =========================
    // Filter
    // =========================
    const handleSubmit = (e) => {
        e.preventDefault();

        if (fromDate > toDate) {
            setError("From date cannot be greater than To date");
            return;
        }

        fetchFoodReport();
    };

    // =========================
    // CSV Export
    // =========================
    const handleExportCSV = () => {
        if (!items.length) return;

        const headers = [
            "Food ID",
            "Set Menu ID",
            "Food Name",
            "Japanese Name",
            "Quantity",
            "Total Sales",
        ];

        const rows = items.map((item) => [
            item.food_id ?? "",
            item.set_menu_id ?? "",
            `"${(item.name || "").replace(/"/g, '""')}"`,
            `"${(item.japanese_name || "").replace(/"/g, '""')}"`,
            Number(item.quantity || 0),
            Number(item.total_sales || 0).toFixed(0),
        ]);

        rows.push([
            "",
            "",
            "Grand Total",
            "",
            summary.quantity,
            summary.sales.toFixed(0),
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob(
            [csvContent],
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.setAttribute(
            "download",
            `food_sales_${fromDate}_${toDate}.csv`
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    // =========================
    // Print
    // =========================
    const handlePrint = () => {
        if (!items.length) return;

        const rows = items
            .map(
                (item, index) => `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${item.name || "-"}</td>
                        <td>${item.japanese_name || "-"}</td>
                        <td>${Number(item.quantity || 0)}</td>
                        <td>¥${Number(item.total_sales || 0).toFixed(0)}</td>
                    </tr>
                `
            )
            .join("");

        const printHTML = `
            <html>
                <head>
                    <title>Food Sales Report</title>

                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            padding: 20px;
                        }

                        h2 {
                            text-align: center;
                            margin-bottom: 5px;
                        }

                        .date {
                            text-align: center;
                            margin-bottom: 20px;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }

                        th,
                        td {
                            border: 1px solid #000;
                            padding: 8px;
                        }

                        th {
                            background: #f2f2f2;
                        }

                        .number {
                            text-align: right;
                        }

                        .total {
                            font-weight: bold;
                        }
                    </style>
                </head>

                <body>

                    <h2>Food Sales Report</h2>

                    <div class="date">
                        ${fromDate} - ${toDate}
                    </div>

                    <table>

                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Food Name</th>
                                <th>Japanese Name</th>
                                <th>Quantity</th>
                                <th>Total Sales</th>
                            </tr>
                        </thead>

                        <tbody>

                            ${rows}

                            <tr class="total">
                                <td colspan="3">
                                    Grand Total
                                </td>

                                <td>
                                    ${summary.quantity}
                                </td>

                                <td>
                                    ¥${summary.sales.toFixed(0)}
                                </td>
                            </tr>

                        </tbody>

                    </table>

                </body>
            </html>
        `;

        const printWindow = window.open(
            "",
            "",
            "width=900,height=700"
        );

        printWindow.document.write(printHTML);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    return (
        <div className="p-4">

            <h2 className="mb-4">
                🍜 Food Sales Report
            </h2>

            {/* =========================
                Date Filter
            ========================= */}

            <Card className="mb-4">
                <Card.Body>

                    <Form onSubmit={handleSubmit}>

                        <Row className="align-items-end g-3">

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>
                                        From Date
                                    </Form.Label>

                                    <Form.Control
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) =>
                                            setFromDate(e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>
                                        To Date
                                    </Form.Label>

                                    <Form.Control
                                        type="date"
                                        value={toDate}
                                        onChange={(e) =>
                                            setToDate(e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>

                                <div className="d-flex gap-2">

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />

                                                Loading...
                                            </>
                                        ) : (
                                            "Filter"
                                        )}
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="success"
                                        onClick={handleExportCSV}
                                        disabled={!items.length}
                                    >
                                        📤 Export CSV
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="info"
                                        onClick={handlePrint}
                                        disabled={!items.length}
                                    >
                                        🖨️ Print
                                    </Button>

                                </div>

                            </Col>

                        </Row>

                    </Form>

                </Card.Body>
            </Card>

            {/* Error */}

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* =========================
                Summary
            ========================= */}

            <Row className="mb-4 g-3">

                <Col md={6}>

                    <Card>
                        <Card.Body>

                            <div className="text-muted">
                                Total Quantity Sold
                            </div>

                            <h3 className="mb-0">
                                {summary.quantity}
                            </h3>

                        </Card.Body>
                    </Card>

                </Col>

                <Col md={6}>

                    <Card>
                        <Card.Body>

                            <div className="text-muted">
                                Total Sales
                            </div>

                            <h3 className="mb-0">
                                ¥{summary.sales.toFixed(0)}
                            </h3>

                        </Card.Body>
                    </Card>

                </Col>

            </Row>

            {/* =========================
                Food Sales Table
            ========================= */}

            <Table
                striped
                bordered
                hover
                responsive
                size="sm"
            >

                <thead>

                <tr>
                    <th>#</th>
                    <th>Food ID</th>
                    <th>Food Name</th>
                    <th>Japanese Name</th>
                    <th className="text-end">
                        Quantity
                    </th>
                    <th className="text-end">
                        Total Sales
                    </th>
                </tr>

                </thead>

                <tbody>

                {items.length > 0 ? (

                    items.map((item, index) => (

                        <tr
                            key={`${item.food_id || "food"}-${item.set_menu_id || "set"}-${index}`}
                        >

                            <td>
                                {index + 1}
                            </td>

                            <td>
                                {item.food_id || "-"}
                            </td>

                            <td>
                                {item.name || "-"}
                            </td>

                            <td>
                                {item.japanese_name || "-"}
                            </td>

                            <td className="text-end">
                                {Number(
                                    item.quantity || 0
                                )}
                            </td>

                            <td className="text-end">
                                ¥
                                {Number(
                                    item.total_sales || 0
                                ).toFixed(0)}
                            </td>

                        </tr>

                    ))

                ) : (

                    <tr>

                        <td
                            colSpan="6"
                            className="text-center"
                        >
                            {loading
                                ? "Loading..."
                                : "No food items found"}
                        </td>

                    </tr>

                )}

                </tbody>

                {items.length > 0 && (

                    <tfoot>

                    <tr
                        style={{
                            fontWeight: "bold",
                            background: "#f8f9fa",
                        }}
                    >

                        <td
                            colSpan="4"
                            className="text-end"
                        >
                            Grand Total:
                        </td>

                        <td className="text-end">
                            {summary.quantity}
                        </td>

                        <td className="text-end">
                            ¥{summary.sales.toFixed(0)}
                        </td>

                    </tr>

                    </tfoot>

                )}

            </Table>

        </div>
    );
};

export default FoodSalesReport;